import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import classNames from "components/ui/classNames";
import { AlertTriangleIcon, ShieldAlertIcon } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { IBrazilianCompanyIssuerRiskCategory } from "types/graphqlTypes";

export default function BrazilianRiskFactorsContent() {
  const issuer = useBrazilianIssuer();
  const { riskFactors } = issuer;

  if (!riskFactors) {
    return null;
  }

  return (
    <div className="h-full min-h-0 w-full space-y-8 overflow-y-auto pr-1 font-aeonik">
      <section className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-800 dark:text-slate-200">
          <ShieldAlertIcon
            aria-hidden={true}
            className="size-5 shrink-0 text-indigo-500 dark:text-indigo-300"
            strokeWidth={2}
          />
          <FormattedMessage defaultMessage="Fatores de Risco" id="wZjqag" />
        </h2>
        <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">{riskFactors.summary}</p>
      </section>

      {riskFactors.risks.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {riskFactors.risks.map((risk, index) => {
            return (
              <li
                key={`${risk.category}-${risk.title}-${index}`}
                className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/70"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={classNames("rounded-full px-2 py-0.5 text-xs font-semibold", {
                      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300":
                        risk.category === IBrazilianCompanyIssuerRiskCategory.Sector,
                      "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300":
                        risk.category === IBrazilianCompanyIssuerRiskCategory.Specific,
                      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300":
                        risk.category === IBrazilianCompanyIssuerRiskCategory.Structural,
                    })}
                  >
                    <RiskCategoryLabel category={risk.category} />
                  </span>

                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">{risk.title}</h3>
                </div>

                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{risk.text}</p>
              </li>
            );
          })}
        </ul>
      )}

      {riskFactors.worstCaseScenario && (
        <section className="rounded-xl bg-amber-50/70 p-4 dark:bg-amber-950/40">
          <h3 className="flex items-center gap-2 text-sm font-bold text-amber-900 dark:text-amber-200">
            <AlertTriangleIcon aria-hidden={true} className="size-4 shrink-0" />
            <FormattedMessage defaultMessage="Pior Cenário" id="ER7TR+" />
          </h3>
          <p className="mt-2 text-sm leading-6 text-amber-950/80 dark:text-amber-100/80">
            {riskFactors.worstCaseScenario}
          </p>
        </section>
      )}
    </div>
  );
}

function RiskCategoryLabel({ category }: { category: IBrazilianCompanyIssuerRiskCategory }) {
  switch (category) {
    case IBrazilianCompanyIssuerRiskCategory.Sector:
      return <FormattedMessage defaultMessage="Setorial" id="mveHmA" />;
    case IBrazilianCompanyIssuerRiskCategory.Specific:
      return <FormattedMessage defaultMessage="Específico" id="WcXPSZ" />;
    case IBrazilianCompanyIssuerRiskCategory.Structural:
      return <FormattedMessage defaultMessage="Estrutural" id="6tMk1f" />;
    default:
      return null;
  }
}
